import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, typeof as _typeof } from '../_virtual/_rollupPluginBabelHelpers.js';
import { utils, useTimeout, useControlled } from '@costar/theme-ui';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import createFilterOptions from '../create-filter-options.esm.js';
import defaultGetOptionDisplayLabel from '../helpers/default-get-option-display-label.esm.js';
import defaultGetOptionLabel from '../helpers/default-get-option-label.esm.js';
import defaultIsSelectAllOption from '../helpers/default-is-select-all-option.esm.js';

var setRef = utils.setRef,
  omit = utils.omit,
  mergeRefs = utils.mergeRefs;

// import { useWhatChanged } from '@simbathesailor/use-what-changed'

function isValidOptionLabel(componentName, propName, option, optionLabel) {
  var retVal = true;
  if (typeof optionLabel !== 'string') {
    retVal = false;
    if (process.env.NODE_ENV !== 'production') {
      var erroneousReturn = optionLabel === undefined ? 'undefined' : "".concat(_typeof(optionLabel), " (").concat(optionLabel, ")");
      console.error("Tui: The `".concat(propName, "` method of ").concat(componentName, " returned ").concat(erroneousReturn, " instead of a string for ").concat(JSON.stringify(option), "."));
    }
  }
  return retVal;
}

// To replace with .findIndex() once we stop IE11 support.
function findIndex(array, comp) {
  for (var i = 0; i < array.length; i += 1) {
    if (comp(array[i])) {
      return i;
    }
  }
  return -1;
}
var defaultFilterOptions = createFilterOptions();
function isSelectAll(option, isSelectAllOption, value, options) {
  return isSelectAllOption(option) && Array.isArray(value) && value.length === options.length - 1;
}

// Number of options to jump in list box when pageup and pagedown keys are used.
var pageSize = 5;
var defaultIsOptionEqualToValue = function defaultIsOptionEqualToValue(option, value) {
  return option === value;
};
var defaultGetTagProps = function defaultGetTagProps() {
  return utils.EMPTY_OBJECT;
};
function useAutocomplete(props) {
  var _inputRef$current;
  var _props$autoComplete = props.autoComplete,
    autoComplete = _props$autoComplete === void 0 ? false : _props$autoComplete,
    _props$autoHighlight = props.autoHighlight,
    autoHighlight = _props$autoHighlight === void 0 ? false : _props$autoHighlight,
    _props$autoSelect = props.autoSelect,
    autoSelect = _props$autoSelect === void 0 ? false : _props$autoSelect,
    _props$blurOnSelect = props.blurOnSelect,
    blurOnSelect = _props$blurOnSelect === void 0 ? false : _props$blurOnSelect,
    _props$clearOnBlur = props.clearOnBlur,
    clearOnBlur = _props$clearOnBlur === void 0 ? !props.freeSolo : _props$clearOnBlur,
    _props$clearOnEscape = props.clearOnEscape,
    clearOnEscape = _props$clearOnEscape === void 0 ? false : _props$clearOnEscape,
    _props$componentName = props.componentName,
    componentName = _props$componentName === void 0 ? 'useAutocomplete' : _props$componentName,
    _props$defaultValue = props.defaultValue,
    defaultValue = _props$defaultValue === void 0 ? null : _props$defaultValue,
    _props$disableClearab = props.disableClearable,
    disableClearable = _props$disableClearab === void 0 ? false : _props$disableClearab,
    _props$disableCloseOn = props.disableCloseOnSelect,
    disableCloseOnSelect = _props$disableCloseOn === void 0 ? false : _props$disableCloseOn,
    disabledProp = props.disabled,
    _props$disabledItemsF = props.disabledItemsFocusable,
    disabledItemsFocusable = _props$disabledItemsF === void 0 ? false : _props$disabledItemsF,
    _props$disableListWra = props.disableListWrap,
    disableListWrap = _props$disableListWra === void 0 ? false : _props$disableListWra,
    _props$filterOptions = props.filterOptions,
    filterOptions = _props$filterOptions === void 0 ? defaultFilterOptions : _props$filterOptions,
    _props$filterSelected = props.filterSelectedOptions,
    filterSelectedOptions = _props$filterSelected === void 0 ? false : _props$filterSelected,
    _props$alwaysFilterOp = props.alwaysFilterOptionsMatchInputValue,
    alwaysFilterOptionsMatchInputValue = _props$alwaysFilterOp === void 0 ? false : _props$alwaysFilterOp,
    _props$freeSolo = props.freeSolo,
    freeSolo = _props$freeSolo === void 0 ? false : _props$freeSolo,
    getOptionDisabled = props.getOptionDisabled,
    _props$getOptionLabel = props.getOptionLabel,
    getOptionLabelProp = _props$getOptionLabel === void 0 ? defaultGetOptionLabel : _props$getOptionLabel,
    _getOptionDisplayLabel = props.getOptionDisplayLabel,
    _props$getTagProps = props.getTagProps,
    _getTagProps = _props$getTagProps === void 0 ? defaultGetTagProps : _props$getTagProps,
    groupBy = props.groupBy,
    _props$handleHomeEndK = props.handleHomeEndKeys,
    handleHomeEndKeys = _props$handleHomeEndK === void 0 ? !props.freeSolo : _props$handleHomeEndK,
    _props$includeInputIn = props.includeInputInList,
    includeInputInList = _props$includeInputIn === void 0 ? false : _props$includeInputIn,
    inputValueProp = props.inputValue,
    _props$isSelectAllOpt = props.isSelectAllOption,
    isSelectAllOption = _props$isSelectAllOpt === void 0 ? defaultIsSelectAllOption : _props$isSelectAllOpt,
    _props$isOptionEqualT = props.isOptionEqualToValue,
    isOptionEqualToValue = _props$isOptionEqualT === void 0 ? defaultIsOptionEqualToValue : _props$isOptionEqualT,
    _props$shouldCheckIsO = props.shouldCheckIsOptionEqualToValue,
    shouldCheckIsOptionEqualToValue = _props$shouldCheckIsO === void 0 ? true : _props$shouldCheckIsO,
    _props$multiple = props.multiple,
    multiple = _props$multiple === void 0 ? false : _props$multiple,
    _props$mode = props.mode,
    mode = _props$mode === void 0 ? 'autocomplete' : _props$mode,
    onBlurProp = props.onBlur,
    onChange = props.onChange,
    onClose = props.onClose,
    onFocusProp = props.onFocus,
    onHighlightChange = props.onHighlightChange,
    onInputChange = props.onInputChange,
    onOpen = props.onOpen,
    openProp = props.open,
    _props$openOnFocus = props.openOnFocus,
    openOnFocus = _props$openOnFocus === void 0 ? false : _props$openOnFocus,
    _props$openOnMouseDow = props.openOnMouseDown,
    openOnMouseDown = _props$openOnMouseDow === void 0 ? true : _props$openOnMouseDow,
    options = props.options,
    _props$readOnly = props.readOnly,
    readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
    _props$selectOnFocus = props.selectOnFocus,
    selectOnFocus = _props$selectOnFocus === void 0 ? !props.freeSolo : _props$selectOnFocus,
    valueProp = props.value,
    maxSelectableTags = props.maxSelectableTags,
    nonListExtendedContentTopRef = props.nonListExtendedContentTopRef,
    nonListExtendedContentRef = props.nonListExtendedContentRef,
    extendedContentTopRef = props.extendedContentTopRef,
    extendedContentRef = props.extendedContentRef;
  var getOptionLabel = useCallback(function (option) {
    var optionLabel = getOptionLabelProp(option);
    if (!isValidOptionLabel(componentName, 'getOptionLabel', option, optionLabel)) {
      return String(optionLabel);
    }
    return optionLabel;
  }, [componentName, getOptionLabelProp]);
  var getOptionDisplayLabel = useMemo(function () {
    return _getOptionDisplayLabel ? _getOptionDisplayLabel : defaultGetOptionDisplayLabel(getOptionLabelProp);
  }, [_getOptionDisplayLabel, getOptionLabelProp]);
  var ignoreFocus = useRef(false);
  var firstFocus = useRef(true);
  var rootRef = useRef(null);
  var inputRef = useRef(null);
  var listboxRef = useRef(null);
  var filteredOptionsRef = useRef([]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    updateGroupOptions = _useState2[0],
    setUpdateGroupOptions = _useState2[1];
  var refreshTimeout = useTimeout();

  //const generatedId = useId(idProp)
  // ssr render will generate a different id, if it exist we want to use the id that has already been set during ssr
  var id = (inputRef === null || inputRef === void 0 ? void 0 : (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.id) || props.id || 'autocomplete';
  // eslint-disable-next-line no-console
  // console.log(`--- use-autocomplete id ${id} ---`)

  var initializedRef = useRef(false);

  // this should only be evaluate on the first load, all the dependencies should not be mutated after the initial load
  var _initialInputValue = useMemo(function () {
    if (initializedRef.current) {
      // eslint-disable-next-line no-console
      console.warn('unexpected mutation of initialized values');
    }
    // only set to true if the initial value undefined, i.e. uncontrolled mode
    initializedRef.current = !initializedRef.current && valueProp === undefined;
    return mode === 'select' && !multiple ? inputValueProp || getOptionDisplayLabel(valueProp) || undefined : inputValueProp;
  }, [getOptionDisplayLabel, inputValueProp, mode, multiple, valueProp]);
  var _useState3 = useState(-1),
    _useState4 = _slicedToArray(_useState3, 2),
    focusedTag = _useState4[0],
    setFocusedTag = _useState4[1];
  var defaultHighlighted = autoHighlight ? 0 : -1;
  var highlightedIndexRef = useRef(defaultHighlighted);
  var _useControlled = useControlled({
      controlled: valueProp,
      "default": defaultValue,
      name: componentName
    }),
    _useControlled2 = _slicedToArray(_useControlled, 3),
    value = _useControlled2[0],
    setValueState = _useControlled2[1],
    valueRef = _useControlled2[2];
  var _useControlled3 = useControlled({
      controlled: _initialInputValue,
      "default": '',
      name: componentName,
      state: 'inputValue'
    }),
    _useControlled4 = _slicedToArray(_useControlled3, 3),
    inputValue = _useControlled4[0],
    setInputValueState = _useControlled4[1],
    inputValueRef = _useControlled4[2];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    focused = _useState6[0],
    setFocused = _useState6[1];
  var resetInputValue = useCallback(function (event, newValue) {
    var value = valueRef.current;
    var inputValue = inputValueRef.current;
    // retain current `inputValue` if new option isn't selected and `clearOnBlur` is false
    // When `multiple` is enabled, `newValue` is an array of all selected items including the newly selected item
    var isOptionSelected = multiple ? Array.isArray(value) && value.length < (Array.isArray(newValue) ? newValue.length : 0) : newValue !== null;
    if (!isOptionSelected && !clearOnBlur) {
      return;
    }
    var newInputValue;
    if (multiple) {
      newInputValue = '';
    } else if (newValue == null) {
      newInputValue = '';
    } else {
      if (mode === 'select' && isSelectAllOption(newValue)) {
        newInputValue = '';
      } else {
        var optionLabel = getOptionDisplayLabel(newValue);
        newInputValue = typeof optionLabel === 'string' ? optionLabel : '';
      }
    }
    if (inputValue === newInputValue) {
      return;
    }
    setInputValueState(newInputValue);
    if (onInputChange) {
      onInputChange(event, newInputValue, 'reset');
    }
  }, [clearOnBlur, getOptionDisplayLabel, inputValueRef, isSelectAllOption, mode, multiple, onInputChange, setInputValueState, valueRef]);
  var prevValue = useRef();
  useEffect(function () {
    var valueChange = value !== prevValue.current;
    prevValue.current = value;
    if (focused && !valueChange) {
      return;
    }

    // Only reset the input's value when freeSolo if the component's value changes.
    if (freeSolo && !valueChange) {
      return;
    }
    resetInputValue(undefined, value);
  }, [value, resetInputValue, focused, prevValue, freeSolo]);
  var _useControlled5 = useControlled({
      controlled: openProp,
      "default": false,
      name: componentName,
      state: 'open'
    }),
    _useControlled6 = _slicedToArray(_useControlled5, 3),
    open = _useControlled6[0],
    setOpenState = _useControlled6[1],
    openRef = _useControlled6[2];
  var _useState7 = useState(true),
    _useState8 = _slicedToArray(_useState7, 2),
    inputPristine = _useState8[0],
    setInputPristine = _useState8[1];
  var popupOpen = open && !readOnly;

  // useWhatChanged(
  //   [
  //     alwaysFilterOptionsMatchInputValue,
  //     filterOptions,
  //     filterSelectedOptions,
  //     getOptionDisplayLabel,
  //     getOptionLabel,
  //     inputPristine,
  //     inputValue,
  //     isOptionEqualToValue,
  //     multiple,
  //     options,
  //     value
  //   ],
  //   'alwaysFilterOptionsMatchInputValue, filterOptions, filterSelectedOptions, getOptionDisplayLabel, getOptionLabel, inputPristine, inputValue, isOptionEqualToValue, multiple, options, value'
  // )

  useEffect(function () {
    var inputValueIsSelectedValue = !multiple && value != null && inputValue === getOptionDisplayLabel(value);
    filteredOptionsRef.current = filterOptions(options.filter(function (option) {
      if (filterSelectedOptions && !!value && (multiple ? value : [value]).some(function (value2) {
        return value2 !== null && isOptionEqualToValue(option, value2);
      })) {
        return false;
      }
      return true;
    }),
    // we use the empty string to manipulate `filterOptions` to not filter any options
    // i.e. the filter predicate always returns true
    {
      inputValue: !alwaysFilterOptionsMatchInputValue && inputValueIsSelectedValue && inputPristine ? '' : inputValue || '',
      getOptionLabel: getOptionLabel
    });
    refreshTimeout(function () {
      return setUpdateGroupOptions(function (state) {
        return !state;
      });
    });
  }, [alwaysFilterOptionsMatchInputValue, filterOptions, filterSelectedOptions, getOptionDisplayLabel, getOptionLabel, inputPristine, inputValue, isOptionEqualToValue, multiple, options, value, refreshTimeout]);
  var listboxAvailable = open && filteredOptionsRef.current.length > 0 && !readOnly;
  if (shouldCheckIsOptionEqualToValue && process.env.NODE_ENV !== 'production') {
    if (value !== null && !freeSolo && options.length > 0) {
      var missingValue = (multiple ? value : [value]).filter(function (value2) {
        return !options.some(function (option) {
          return isOptionEqualToValue(option, value2);
        });
      });
      if (missingValue.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(["Tui: The value provided to ".concat(componentName, " is invalid."), "None of the options match with `".concat(missingValue.length > 1 ? JSON.stringify(missingValue) : JSON.stringify(missingValue[0]), "`."), 'You can use the `isOptionEqualToValue` prop to customize the equality test.'].join('\n'));
      }
    }
  }
  var focusTag = useCallback(function (tagToFocus) {
    // eslint-disable-next-line no-console
    // console.log(`--- use-autocomplete focusTag() ---`)
    if (tagToFocus === -1) {
      var _inputRef$current2;
      (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.focus();
    } else {
      var element = null;
      if (rootRef.current) {
        element = rootRef.current.querySelector("[data-tag-index=\"".concat(tagToFocus, "\"]"));
      }
      if (element) {
        element.focus();
      }
    }
  }, []);

  // Ensure the focusedTag is never inconsistent
  useEffect(function () {
    if (multiple && Array.isArray(value) && focusedTag > value.length - 1) {
      setFocusedTag(-1);
      focusTag(-1);
    }
  }, [value, multiple, focusedTag, focusTag]);
  var validOptionIndex = useCallback(function (index, direction) {
    if (!listboxRef.current || index === -1) {
      return -1;
    }
    var nextFocus = index;
    while (true) {
      // Out of range
      if (direction === 'next' && nextFocus === filteredOptionsRef.current.length || direction === 'previous' && nextFocus === -1) {
        return -1;
      }
      var option = listboxRef.current.querySelector("[data-option-index=\"".concat(nextFocus, "\"]"));

      // Same logic as MenuList.js
      var nextFocusDisabled = disabledItemsFocusable ? false : !option || option.disabled || option.getAttribute('aria-disabled') === 'true';
      if (option && !option.hasAttribute('tabindex') || nextFocusDisabled) {
        // Move to the next element.
        nextFocus += direction === 'next' ? 1 : -1;
      } else {
        return nextFocus;
      }
    }
  }, [disabledItemsFocusable, filteredOptionsRef]);
  var setHighlightedIndex = useCallback(function (_ref) {
    var event = _ref.event,
      index = _ref.index,
      _ref$reason = _ref.reason,
      reason = _ref$reason === void 0 ? 'auto' : _ref$reason;
    // eslint-disable-next-line no-console
    // console.log(
    //   `--- use-autocomplete setHighlightedIndex() index: ${index} reason: ${reason} ---`
    // )
    highlightedIndexRef.current = index;
    if (inputRef.current) {
      // does the index exist?
      if (index === -1) {
        inputRef.current.removeAttribute('aria-activedescendant');
      } else {
        inputRef.current.setAttribute('aria-activedescendant', "".concat(id, "-option-").concat(index));
      }
    }
    if (onHighlightChange) {
      onHighlightChange(event, index === -1 ? null : filteredOptionsRef.current[index], reason);
    }
    if (listboxRef.current) {
      var prev = listboxRef.current.querySelector('[role="option"].csg-tui-focused');
      if (prev) {
        prev.classList.remove('csg-tui-focused');
        prev.classList.remove('csg-tui-focusVisible');
      }
      var listboxNode = listboxRef.current.parentElement ? listboxRef.current.parentElement.querySelector('[role="listbox"]') : null;

      // "No results"
      if (!listboxNode) {
        return;
      }
      if (index === -1) {
        listboxNode.scrollTop = 0;
        return;
      }
      var option = listboxRef.current.querySelector("[data-option-index=\"".concat(index, "\"]"));
      if (!option) {
        return;
      }
      option.classList.add('csg-tui-focused');
      if (reason === 'keyboard') {
        option.classList.add('csg-tui-focusVisible');
      }

      // Scroll active descendant into view.
      // Logic copied from https://www.w3.org/TR/wai-aria-practices/examples/listbox/js/listbox.js
      //
      // Consider this API instead once it has a better browser support:
      // .scrollIntoView({ scrollMode: 'if-needed', block: 'nearest' });
      if (listboxNode.scrollHeight > listboxNode.clientHeight && reason !== 'mouse') {
        var element = option;
        var scrollBottom = listboxNode.clientHeight + listboxNode.scrollTop;
        var elementBottom = element.offsetTop + element.offsetHeight;
        if (elementBottom > scrollBottom) {
          listboxNode.scrollTop = elementBottom - listboxNode.clientHeight;
        } else if (element.offsetTop - element.offsetHeight * (groupBy ? 1.3 : 0) < listboxNode.scrollTop) {
          listboxNode.scrollTop = element.offsetTop - element.offsetHeight * (groupBy ? 1.3 : 0);
        }
      }
    }
  }, [groupBy, id, onHighlightChange]);

  // useWhatChanged(
  //   [
  //     autoComplete,
  //     defaultHighlighted,
  //     disableListWrap,
  //     filteredOptions,
  //     getOptionLabel,
  //     includeInputInList,
  //     popupOpen,
  //     setHighlightedIndex,
  //     validOptionIndex
  //   ],
  //   'autoComplete, defaultHighlighted, disableListWrap, filteredOptions, getOptionLabel, includeInputInList, popupOpen, setHighlightedIndex,  validOptionIndex'
  // )

  var changeHighlightedIndex = useCallback(function (_ref2) {
    var _inputValueRef$curren;
    var event = _ref2.event,
      diff = _ref2.diff,
      _ref2$direction = _ref2.direction,
      direction = _ref2$direction === void 0 ? 'next' : _ref2$direction,
      _ref2$reason = _ref2.reason,
      reason = _ref2$reason === void 0 ? 'auto' : _ref2$reason;
    // eslint-disable-next-line no-console
    // console.log(
    //   `--- use-autocomplete changeHighlightedIndex() diff: ${diff} direction: ${direction} reason: ${reason} ---`
    // )
    if (!popupOpen) {
      return;
    }
    var inputValue = (_inputValueRef$curren = inputValueRef.current) !== null && _inputValueRef$curren !== void 0 ? _inputValueRef$curren : '';
    var getNextIndex = function getNextIndex() {
      var maxIndex = filteredOptionsRef.current.length - 1;
      if (diff === 'reset') {
        return defaultHighlighted;
      }
      if (diff === 'start') {
        return 0;
      }
      if (diff === 'end') {
        return maxIndex;
      }
      var newIndex = highlightedIndexRef.current + diff;
      if (newIndex < 0) {
        if (newIndex === -1 && includeInputInList) {
          var _inputRef$current3;
          (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.select();
          return -1;
        }
        if (disableListWrap && highlightedIndexRef.current !== -1 || Math.abs(diff) > 1) {
          return 0;
        }
        return maxIndex;
      }
      if (newIndex > maxIndex) {
        if (newIndex === maxIndex + 1 && includeInputInList) {
          var _inputRef$current4;
          (_inputRef$current4 = inputRef.current) === null || _inputRef$current4 === void 0 ? void 0 : _inputRef$current4.select();
          return -1;
        }
        if (disableListWrap || Math.abs(diff) > 1) {
          return maxIndex;
        }
        return 0;
      }
      return newIndex;
    };
    var nextIndex = validOptionIndex(getNextIndex(), direction);
    setHighlightedIndex({
      index: nextIndex,
      reason: reason,
      event: event
    });

    // Sync the content of the input with the highlighted option.
    if (autoComplete && diff !== 'reset') {
      var curElement = inputRef.current;
      if (curElement) {
        if (nextIndex === -1) {
          curElement.value = inputValue;
        } else {
          // FIXME(don): option could be a react element
          var option = getOptionLabel(filteredOptionsRef.current[nextIndex]);
          curElement.value = option;

          // The portion of the selected suggestion that has not been typed by the user,
          // a completion string, appears inline after the input cursor in the textbox.
          var index = option.toLowerCase().indexOf(inputValue.toLowerCase());
          if (index === 0 && inputValue.length > 0) {
            curElement.setSelectionRange(inputValue.length, option.length);
          }
        }
      }
    }
  }, [autoComplete, defaultHighlighted, disableListWrap, getOptionLabel, includeInputInList, inputValueRef, popupOpen, setHighlightedIndex, validOptionIndex]);

  // useWhatChanged(
  //   [
  //     changeHighlightedIndex,
  //     filteredOptions,
  //     isOptionEqualToValue,
  //     multiple,
  //     popupOpen,
  //     setHighlightedIndex,
  //     value
  //   ],
  //   'changeHighlightedIndex, filteredOptions, isOptionEqualToValue, multiple, popupOpen, setHighlightedIndex, value'
  // )

  var syncHighlightedIndex = useCallback(function () {
    if (!popupOpen) {
      return;
    }
    var valueItem = multiple && Array.isArray(valueRef.current) ? valueRef.current[0] : valueRef.current;

    // The popup is empty, reset
    if (filteredOptionsRef.current.length === 0 || valueItem == null) {
      changeHighlightedIndex({
        diff: 'reset'
      });
      return;
    }
    if (!listboxRef.current) {
      return;
    }

    // Synchronize the value with the highlighted index
    if (valueItem != null) {
      var currentOption = filteredOptionsRef.current[highlightedIndexRef.current];

      // Keep the current highlighted index if possible
      if (multiple && currentOption && findIndex(valueRef.current, function (val) {
        return isOptionEqualToValue(currentOption, val);
      }) !== -1) {
        return;
      }
      var itemIndex = findIndex(filteredOptionsRef.current, function (optionItem) {
        return isOptionEqualToValue(optionItem, valueItem);
      });
      if (itemIndex === -1) {
        changeHighlightedIndex({
          diff: 'reset'
        });
      } else {
        setHighlightedIndex({
          index: itemIndex
        });
      }
      return;
    }

    // Prevent the highlighted index to leak outside the boundaries.
    if (highlightedIndexRef.current >= filteredOptionsRef.current.length - 1) {
      setHighlightedIndex({
        index: filteredOptionsRef.current.length - 1
      });
      return;
    }

    // Restore the focus to the previous index.
    setHighlightedIndex({
      index: highlightedIndexRef.current
    });
    // Ignore filteredOptions (and options, isOptionEqualToValue, getOptionLabel) not to break the scroll position
  }, [changeHighlightedIndex, isOptionEqualToValue, multiple, popupOpen, setHighlightedIndex, valueRef]);
  var handleListboxRef = useCallback(function (node) {
    setRef(listboxRef, node);
    if (!node) {
      return;
    }
    syncHighlightedIndex();
  }, [syncHighlightedIndex]);
  if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(function () {
      if (!inputRef.current || inputRef.current.nodeName !== 'INPUT') {
        var _inputRef$current5;
        if (((_inputRef$current5 = inputRef.current) === null || _inputRef$current5 === void 0 ? void 0 : _inputRef$current5.nodeName) === 'TEXTAREA') {
          // eslint-disable-next-line no-console
          console.warn(["A textarea element was provided to ".concat(componentName, " where input was expected."), "This is not a supported scenario but it may work under certain conditions.", "A textarea keyboard navigation may conflict with Autocomplete controls (e.g. enter and arrow keys).", "Make sure to test keyboard navigation and add custom event handlers if necessary."].join('\n'));
        } else {
          console.error(["Tui: Unable to find the input element. It was resolved to ".concat(inputRef.current, " while an HTMLInputElement was expected."), "Instead, ".concat(componentName, " expects an input element."), '', componentName === 'useAutocomplete' ? 'Make sure you have binded getInputProps correctly and that the normal ref/effect resolutions order is guaranteed.' : 'Make sure you have customized the input component correctly.'].join('\n'));
        }
      }
    }, [componentName]);
  }
  useEffect(function () {
    syncHighlightedIndex();
  }, [syncHighlightedIndex]);
  var isMaxTagsSelected = mode === 'autocomplete' && typeof maxSelectableTags === 'number' && maxSelectableTags > 0 && Array.isArray(value) && value.length >= maxSelectableTags;
  var handleOpen = useCallback(function (event) {
    if (openRef.current) {
      return;
    }
    if (isMaxTagsSelected) {
      return;
    }
    // eslint-disable-next-line no-console
    // console.log(`--- use-autocomplete handleOpen()`)
    setOpenState(true);
    setInputPristine(true);
    if (onOpen) {
      onOpen(event);
    }
  }, [isMaxTagsSelected, onOpen, openRef, setOpenState]);
  var handleClose = useCallback(function (event, reason) {
    if (!openRef.current) {
      return;
    }
    // eslint-disable-next-line no-console
    // console.log(`--- use-autocomplete handleClose() ---`)
    setOpenState(false);
    if (onClose) {
      onClose(event, reason);
    }
  }, [onClose, openRef, setOpenState]);
  var handlePopupExited = useCallback(function () {
    if (blurOnSelect === true || blurOnSelect === 'touch' && isTouch.current || blurOnSelect === 'mouse' && !isTouch.current) {
      var _inputRef$current6;
      // eslint-disable-next-line no-console
      // console.log(`--- use-autocomplete ${inputRef?.current} blur() ---`)
      (_inputRef$current6 = inputRef.current) === null || _inputRef$current6 === void 0 ? void 0 : _inputRef$current6.blur();
    }
  }, [blurOnSelect]);
  var handleValue = useCallback(function (event, newValue, reason, details) {
    var value = valueRef.current;
    if (Array.isArray(value) && Array.isArray(newValue)) {
      if (value.length === newValue.length && value.every(function (val, i) {
        return val === newValue[i];
      })) {
        return;
      }
    } else if (value === newValue) {
      return;
    }
    if (onChange) {
      onChange(event, newValue, reason, details);
    }
    // eslint-disable-next-line no-console
    // console.log(
    //   `---- handleValue setValueState: /n ${JSON.stringify(
    //     newValue,
    //     undefined,
    //     2
    //   )}) ----`
    // )
    setValueState(newValue);
  }, [onChange, setValueState, valueRef]);
  var isTouch = useRef(false);

  // useWhatChanged(
  //   [
  //     componentName,
  //     disableCloseOnSelect,
  //     handleClose,
  //     handleValue,
  //     isSelectAllOption,
  //     isOptionEqualToValue,
  //     mode,
  //     multiple,
  //     options,
  //     resetInputValue,
  //     valueRef
  //   ],
  //   'componentName, disableCloseOnSelect, handleClose, handleValue, isSelectAllOption, isOptionEqualToValue, mode, multiple, options, resetInputValue, valueRef'
  // )

  var selectNewValue = useCallback(function (event, option) {
    var reasonProp = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'selectOption';
    var origin = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'options';
    var reason = reasonProp;
    var newValue = option;
    var value = valueRef.current;
    var isClearAll = mode === 'select' && multiple && isSelectAll(newValue, isSelectAllOption, value, options);
    if (multiple) {
      newValue = isClearAll ? [] : Array.isArray(value) ? value.slice() : [];
      if (process.env.NODE_ENV !== 'production') {
        var matches = newValue.filter(function (val) {
          return isOptionEqualToValue(option, val);
        });
        if (matches.length > 1) {
          console.error(["Tui: The `isOptionEqualToValue` method of ".concat(componentName, " do not handle the arguments correctly."), "The component expects a single value to match a given option but found ".concat(matches.length, " matches.")].join('\n'));
        }
      }
      var itemIndex = findIndex(newValue, function (valueItem) {
        return isOptionEqualToValue(option, valueItem);
      });
      if (!isClearAll && itemIndex === -1) {
        newValue.push(option);
      } else if (origin !== 'freeSolo') {
        newValue.splice(itemIndex, 1);
        reason = 'removeOption';
      }
    }
    resetInputValue(event, newValue);
    if (mode === 'select') {
      if (multiple && Array.isArray(newValue)) {
        var nonOptions = newValue.filter(function (i) {
          return isSelectAllOption(i);
        });
        if (nonOptions.length > 0) {
          var allOptions = options.filter(function (i) {
            return !isSelectAllOption(i);
          });
          newValue = allOptions;
        }
      } else if (isClearAll) {
        newValue = reason = 'clear';
      }
    }
    handleValue(event, newValue, reason, {
      option: option
    });
    if (!disableCloseOnSelect && !event.ctrlKey && !event.metaKey) {
      handleClose(event, reason);
    }
  }, [componentName, disableCloseOnSelect, handleClose, handleValue, isOptionEqualToValue, isSelectAllOption, mode, multiple, options, resetInputValue, valueRef]);
  var validTagIndex = useCallback(function (index, direction) {
    if (index === -1) {
      return -1;
    }
    var nextFocus = index;
    while (true) {
      var _rootRef$current;
      // Out of range
      if (direction === 'next' && Array.isArray(value) && nextFocus === value.length || direction === 'previous' && nextFocus === -1) {
        return -1;
      }
      var option = (_rootRef$current = rootRef.current) === null || _rootRef$current === void 0 ? void 0 : _rootRef$current.querySelector("[data-tag-index=\"".concat(nextFocus, "\"]"));

      // Same logic as MenuList.js
      if (!option || !option.hasAttribute('tabindex') || option.disabled || option.getAttribute('aria-disabled') === 'true') {
        nextFocus += direction === 'next' ? 1 : -1;
      } else {
        return nextFocus;
      }
    }
  }, [value]);
  var handleFocusTag = useCallback(function (event, direction) {
    if (!multiple) {
      return;
    }
    handleClose(event, 'toggleInput');
    var nextTag = focusedTag;
    if (focusedTag === -1) {
      if (inputValue === '' && direction === 'previous' && Array.isArray(value)) {
        nextTag = value.length - 1;
      }
    } else {
      nextTag += direction === 'next' ? 1 : -1;
      if (nextTag < 0) {
        nextTag = 0;
      }
      if (Array.isArray(value) && nextTag === value.length) {
        nextTag = -1;
      }
    }
    nextTag = validTagIndex(nextTag, direction);
    setFocusedTag(nextTag);
    focusTag(nextTag);
  }, [focusTag, focusedTag, handleClose, inputValue, multiple, validTagIndex, value]);
  var handleClear = useCallback(function (event) {
    ignoreFocus.current = true;
    setInputValueState('');
    if (onInputChange) {
      onInputChange(event, '', 'clear');
    }
    handleValue(event, multiple ? [] : null, 'clear');
  }, [handleValue, multiple, onInputChange, setInputValueState]);

  // useWhatChanged(
  //   [
  //     autoComplete,
  //     changeHighlightedIndex,
  //     clearOnEscape,
  //     focusTag,
  //     focusedTag,
  //     freeSolo,
  //     getOptionDisabled,
  //     getOptionDisplayLabel,
  //     handleClear,
  //     handleClose,
  //     handleFocusTag,
  //     handleHomeEndKeys,
  //     handleOpen,
  //     handleValue,
  //     inputValue,
  //     mode,
  //     multiple,
  //     popupOpen,
  //     readOnly,
  //     selectNewValue,
  //     value
  //   ],
  //   'autoComplete, changeHighlightedIndex, clearOnEscape, focusTag, focusedTag, freeSolo, getOptionDisabled, getOptionDisplayLabel, handleClear, handleClose, handleFocusTag, handleHomeEndKeys, handleOpen, handleValue, inputValue, mode, multiple, popupOpen, readOnly, selectNewValue, value'
  // )

  var handleKeyDown = useCallback(function (other) {
    return function (event) {
      var inputValueIsSelectedValue = !multiple && value != null && inputValue === getOptionDisplayLabel(value);
      if (other.onKeyDown) {
        other.onKeyDown(event);
      }
      if (event.defaultTuiPrevented) {
        return;
      }
      if (focusedTag !== -1 && ['ArrowLeft', 'ArrowRight'].indexOf(event.key) === -1) {
        setFocusedTag(-1);
        focusTag(-1);
      }

      // Wait until IME is settled.
      if (event.which !== 229) {
        switch (event.key) {
          case ' ':
            if (mode === 'select') {
              event.preventDefault();
              handleOpen(event);
            }
            break;
          case 'Home':
            if (popupOpen && handleHomeEndKeys) {
              changeHighlightedIndex({
                diff: 'start',
                direction: 'next',
                reason: 'keyboard',
                event: event
              });
            }
            break;
          case 'End':
            if (popupOpen && handleHomeEndKeys) {
              changeHighlightedIndex({
                diff: 'end',
                direction: 'previous',
                reason: 'keyboard',
                event: event
              });
            }
            break;
          case 'PageUp':
            changeHighlightedIndex({
              diff: -pageSize,
              direction: 'previous',
              reason: 'keyboard',
              event: event
            });
            handleOpen(event);
            break;
          case 'PageDown':
            changeHighlightedIndex({
              diff: pageSize,
              direction: 'next',
              reason: 'keyboard',
              event: event
            });
            handleOpen(event);
            break;
          case 'ArrowDown':
            changeHighlightedIndex({
              diff: 1,
              direction: 'next',
              reason: 'keyboard',
              event: event
            });
            handleOpen(event);
            break;
          case 'ArrowUp':
            changeHighlightedIndex({
              diff: -1,
              direction: 'previous',
              reason: 'keyboard',
              event: event
            });
            handleOpen(event);
            break;
          case 'ArrowLeft':
            handleFocusTag(event, 'previous');
            break;
          case 'ArrowRight':
            handleFocusTag(event, 'next');
            break;
          case 'Enter':
            if (highlightedIndexRef.current !== -1 && popupOpen) {
              var option = filteredOptionsRef.current[highlightedIndexRef.current];
              var disabled = getOptionDisabled ? getOptionDisabled(option) : false;

              // Avoid early form validation, let the end-users continue filling the form.
              event.preventDefault();
              if (disabled) {
                return;
              }
              selectNewValue(event, option, 'selectOption');

              // Move the selection to the end.
              if (autoComplete) {
                var _inputRef$current7;
                (_inputRef$current7 = inputRef.current) === null || _inputRef$current7 === void 0 ? void 0 : _inputRef$current7.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
              }
            } else if (freeSolo && inputValue !== '' && inputValueIsSelectedValue === false) {
              if (multiple) {
                // Allow people to add new values before they submit the form.
                event.preventDefault();
              }
              selectNewValue(event, inputValue, 'createOption', 'freeSolo');
            }
            break;
          case 'Escape':
            if (popupOpen) {
              // Avoid Opera to exit fullscreen mode.
              event.preventDefault();
              // Avoid the Modal to handle the event.
              event.stopPropagation();
              handleClose(event, 'escape');
            } else if (clearOnEscape && (inputValue !== '' || multiple && Array.isArray(value) && value.length > 0)) {
              // Avoid Opera to exit fullscreen mode.
              event.preventDefault();
              // Avoid the Modal to handle the event.
              event.stopPropagation();
              handleClear(event);
            }
            break;
          case 'Backspace':
            if (multiple && !readOnly && inputValue === '' && Array.isArray(value) && value.length > 0) {
              var index = focusedTag === -1 ? value.length - 1 : focusedTag;
              var newValue = value.slice();
              newValue.splice(index, 1);
              handleValue(event, newValue, 'removeOption', {
                option: value[index]
              });
            }
            break;
        }
      }
    };
  }, [autoComplete, changeHighlightedIndex, clearOnEscape, focusTag, focusedTag, freeSolo, getOptionDisabled, getOptionDisplayLabel, handleClear, handleClose, handleFocusTag, handleHomeEndKeys, handleOpen, handleValue, inputValue, mode, multiple, popupOpen, readOnly, selectNewValue, value]);
  var selectOnFirstFocus = useCallback(function () {
    var _inputRef$current8, _inputRef$current$sel, _inputRef$current$sel2;
    // eslint-disable-next-line no-console
    // console.log(`--- use-autocomplete selectOnFirstFocus() ---`)
    (_inputRef$current8 = inputRef.current) === null || _inputRef$current8 === void 0 ? void 0 : _inputRef$current8.focus();
    if (selectOnFocus && mode !== 'select' && firstFocus.current && inputRef.current !== null && ((_inputRef$current$sel = inputRef.current.selectionEnd) !== null && _inputRef$current$sel !== void 0 ? _inputRef$current$sel : 0) - ((_inputRef$current$sel2 = inputRef.current.selectionStart) !== null && _inputRef$current$sel2 !== void 0 ? _inputRef$current$sel2 : 0) === 0) {
      inputRef.current.select();
    }
    firstFocus.current = false;
  }, [mode, selectOnFocus]);
  var handleFocus = useCallback(function (event) {
    // eslint-disable-next-line no-console
    // console.log(`--- use-autocomplete handleFocus() ---`)
    if (!openOnMouseDown && selectOnFocus) {
      selectOnFirstFocus();
    }
    setFocused(true);
    onFocusProp && onFocusProp(event);
    if (openOnFocus && !ignoreFocus.current) {
      handleOpen(event);
    }
  }, [handleOpen, onFocusProp, openOnFocus, openOnMouseDown, selectOnFirstFocus, selectOnFocus]);
  var handleBlur = useCallback(function (event) {
    var _listboxRef$current, _listboxRef$current$p, _nonListExtendedConte, _nonListExtendedConte2, _nonListExtendedConte3, _nonListExtendedConte4, _extendedContentTopRe, _extendedContentRef$c;
    // eslint-disable-next-line no-console
    // console.log(`--- use-autocomplete handleBlur() ---`)
    // Ignore the event when using the scrollbar with IE11
    if ((_listboxRef$current = listboxRef.current) !== null && _listboxRef$current !== void 0 && (_listboxRef$current$p = _listboxRef$current.parentElement) !== null && _listboxRef$current$p !== void 0 && _listboxRef$current$p.contains(document.activeElement)) {
      var _inputRef$current9;
      (_inputRef$current9 = inputRef.current) === null || _inputRef$current9 === void 0 ? void 0 : _inputRef$current9.focus();
      return;
    }
    if (nonListExtendedContentTopRef !== null && nonListExtendedContentTopRef !== void 0 && nonListExtendedContentTopRef.current && nonListExtendedContentTopRef !== null && nonListExtendedContentTopRef !== void 0 && (_nonListExtendedConte = nonListExtendedContentTopRef.current) !== null && _nonListExtendedConte !== void 0 && (_nonListExtendedConte2 = _nonListExtendedConte.parentElement) !== null && _nonListExtendedConte2 !== void 0 && _nonListExtendedConte2.contains(event === null || event === void 0 ? void 0 : event.relatedTarget) || nonListExtendedContentRef !== null && nonListExtendedContentRef !== void 0 && nonListExtendedContentRef.current && nonListExtendedContentRef !== null && nonListExtendedContentRef !== void 0 && (_nonListExtendedConte3 = nonListExtendedContentRef.current) !== null && _nonListExtendedConte3 !== void 0 && (_nonListExtendedConte4 = _nonListExtendedConte3.parentElement) !== null && _nonListExtendedConte4 !== void 0 && _nonListExtendedConte4.contains(event === null || event === void 0 ? void 0 : event.relatedTarget) || extendedContentTopRef !== null && extendedContentTopRef !== void 0 && extendedContentTopRef.current && extendedContentTopRef !== null && extendedContentTopRef !== void 0 && (_extendedContentTopRe = extendedContentTopRef.current) !== null && _extendedContentTopRe !== void 0 && _extendedContentTopRe.contains(event === null || event === void 0 ? void 0 : event.relatedTarget) || extendedContentRef !== null && extendedContentRef !== void 0 && extendedContentRef.current && extendedContentRef !== null && extendedContentRef !== void 0 && (_extendedContentRef$c = extendedContentRef.current) !== null && _extendedContentRef$c !== void 0 && _extendedContentRef$c.contains(event === null || event === void 0 ? void 0 : event.relatedTarget)) {
      var _inputRef$current10;
      (_inputRef$current10 = inputRef.current) === null || _inputRef$current10 === void 0 ? void 0 : _inputRef$current10.focus();
      return;
    }
    onBlurProp && onBlurProp(event);
    setFocused(false);
    firstFocus.current = true;
    ignoreFocus.current = false;
    if (autoSelect && highlightedIndexRef.current !== -1 && popupOpen) {
      selectNewValue(event, filteredOptionsRef.current[highlightedIndexRef.current], 'blur');
    } else if (autoSelect && freeSolo && inputValue !== '') {
      selectNewValue(event, inputValue, 'blur', 'freeSolo');
    } else if (clearOnBlur) {
      resetInputValue(event, value);
    }
    handleClose(event, 'blur');
  }, [autoSelect, clearOnBlur, extendedContentRef, extendedContentTopRef, freeSolo, handleClose, inputValue, nonListExtendedContentRef, nonListExtendedContentTopRef, onBlurProp, popupOpen, resetInputValue, selectNewValue, value]);
  var handleInputChange = useCallback(function (event) {
    if (isMaxTagsSelected) {
      return;
    }
    var newValue = 'unmaskedValue' in (event !== null && event !== void 0 ? event : {}) ? event.unmaskedValue // when input has masking this will be the field containing the current value.
    : (event === null || event === void 0 ? void 0 : event.target).value;
    if (inputValue !== newValue) {
      setInputValueState(newValue);
      setInputPristine(false);
      if (onInputChange) {
        onInputChange(event, newValue, 'input');
      }
    }
    if (newValue === '') {
      if (!disableClearable && !multiple) {
        handleValue(event, null, 'clear');
      }
    } else {
      handleOpen(event);
    }
  }, [disableClearable, handleOpen, handleValue, inputValue, isMaxTagsSelected, multiple, onInputChange, setInputValueState]);
  var handleOptionMouseOver = useCallback(function (event) {
    setHighlightedIndex({
      event: event,
      index: Number(event === null || event === void 0 ? void 0 : event.currentTarget.getAttribute('data-option-index')),
      reason: 'mouse'
    });
  }, [setHighlightedIndex]);
  var handleOptionTouchStart = useCallback(function () {
    isTouch.current = true;
  }, []);
  var handleOptionClick = useCallback(function (event) {
    var index = Number(event === null || event === void 0 ? void 0 : event.currentTarget.getAttribute('data-option-index'));
    selectNewValue(event, filteredOptionsRef.current[index], 'selectOption');
    isTouch.current = false;
  }, [selectNewValue]);
  var handleTagDelete = useCallback(function (index) {
    return function (event) {
      if (Array.isArray(value)) {
        var newValue = value.slice();
        newValue.splice(index, 1);
        handleValue(event, newValue, 'removeOption', {
          option: value[index]
        });
      }
    };
  }, [handleValue, value]);
  var handlePopupIndicator = useCallback(function (event) {
    if (open) {
      handleClose(event, 'toggleInput');
    } else {
      handleOpen(event);
    }
  }, [handleClose, handleOpen, open]);
  var handlePopupEntering = useCallback(function () {
    selectOnFirstFocus();
  }, [selectOnFirstFocus]);
  var handleInputMouseDown = useCallback(function (event) {
    if (openOnMouseDown && (inputValue === '' || !open)) {
      handlePopupIndicator(event);
    }
  }, [handlePopupIndicator, inputValue, open, openOnMouseDown]);
  var handleInputMouseUp = useCallback(function (event) {
    // Prevent canceling of the onMouseDown behavior see fix(2006180)
    event === null || event === void 0 ? void 0 : event.preventDefault();
  }, []);
  var isMultiSelect = mode === 'select' && multiple;

  // Prevent input blur when interacting with the combobox
  var handleMouseDown = useCallback(function (event) {
    if ((event === null || event === void 0 ? void 0 : event.target).getAttribute('id') !== id) {
      event === null || event === void 0 ? void 0 : event.preventDefault();

      // Open popover in multiselect. Mutiselect (only) uses a span to display selected values
      // as a result mousedown event needs to be used to open the popover
      if (isMultiSelect) {
        handleInputMouseDown();
      }
    }
  }, [handleInputMouseDown, id, isMultiSelect]);
  var dirty = (inputValue || '').length > 0;
  dirty = dirty || (multiple && Array.isArray(value) ? value.length > 0 : value !== null);
  var groupedOptions = useMemo(function () {
    var result = filteredOptionsRef.current;
    if (groupBy) {
      // used to keep track of key and indexes in the result array
      var indexBy = new Map();
      var warn = false;
      result = filteredOptionsRef.current.reduce(function (acc, option, index) {
        var group = groupBy(option);
        if (acc.length > 0 && acc[acc.length - 1].group === group) {
          acc[acc.length - 1].options.push(option);
        } else {
          if (process.env.NODE_ENV !== 'production') {
            if (indexBy.get(group) && !warn) {
              // eslint-disable-next-line no-console
              console.warn("Tui: The options provided combined with the `groupBy` method of ".concat(componentName, " returns duplicated headers."), 'You can solve the issue by sorting the options with the output of `groupBy`.');
              warn = true;
            }
            indexBy.set(group, true);
          }
          acc.push({
            key: index,
            index: index,
            group: group,
            options: [option]
          });
        }
        return acc;
      }, []);
    }
    return result;
  }, [componentName, groupBy, updateGroupOptions]);
  if (disabledProp && focused) {
    handleBlur();
  }
  var getRootProps = useCallback(function () {
    var other = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var externRef = other.ref;
    return _objectSpread2(_objectSpread2({
      ref: mergeRefs(rootRef, externRef),
      'aria-owns': listboxAvailable ? "".concat(id, "-listbox") : undefined
    }, omit(other, ['ref'])), {}, {
      onKeyDown: handleKeyDown(other),
      onMouseDown: handleMouseDown
    });
  }, [handleKeyDown, handleMouseDown, id, listboxAvailable]);
  var getInputLabelProps = useCallback(function () {
    return {
      id: "".concat(id, "-label"),
      htmlFor: id
    };
  }, [id]);
  var getInputProps = useCallback(function () {
    return {
      id: id,
      value: inputValue !== null && inputValue !== void 0 ? inputValue : undefined,
      title: popupOpen ? '' : inputValue !== null && inputValue !== void 0 ? inputValue : undefined,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onChange: handleInputChange,
      onMouseDown: handleInputMouseDown,
      onMouseUp: handleInputMouseUp,
      // if open then this is handled imperatively so don't let react override
      // only have an opinion about this when closed
      'aria-activedescendant': popupOpen ? '' : undefined,
      'aria-autocomplete': autoComplete ? 'both' : 'list',
      'aria-controls': listboxAvailable ? "".concat(id, "-listbox") : undefined,
      'aria-expanded': listboxAvailable,
      // Disable browser's suggestion that might overlap with the popup.
      // Handle autocomplete but not autofill.
      ref: inputRef,
      autoCapitalize: 'none',
      spellCheck: 'false',
      role: 'combobox'
    };
  }, [autoComplete, handleBlur, handleFocus, handleInputChange, handleInputMouseDown, handleInputMouseUp, id, inputValue, listboxAvailable, popupOpen]);
  var getClearProps = useCallback(function () {
    return {
      tabIndex: -1,
      onClick: handleClear
    };
  }, [handleClear]);
  var getPopupIndicatorProps = useCallback(function () {
    return {
      tabIndex: -1,
      onClick: function onClick(e) {
        // getPopupIndicator().onClick is the complement of getRootProps().onMouseDown
        // In multiSelect, getPopupIndicatorProps().onClick and getRootProps().onMouseDown fire resulting in the popover opening then immediately closing after popup indicator clicked
        // To prevent this we only fire the popupIndicator.onClick handler in non multiSelect mode to prevent this bug
        if (!isMultiSelect) {
          handlePopupIndicator(e);
        }
      }
    };
  }, [handlePopupIndicator, isMultiSelect]);
  var getTagProps = useCallback(function (_ref3) {
    var index = _ref3.index,
      option = _ref3.option;
    return _objectSpread2(_objectSpread2({
      key: index,
      'data-tag-index': index,
      tabIndex: -1
    }, !readOnly && {
      onRemove: handleTagDelete(index)
    }), _getTagProps({
      index: index,
      option: option
    }));
  }, [_getTagProps, handleTagDelete, readOnly]);
  var getListboxProps = useCallback(function () {
    return {
      role: 'listbox',
      id: "".concat(id, "-listbox"),
      'aria-labelledby': "".concat(id, "-label"),
      ref: handleListboxRef,
      onMouseDown: function onMouseDown(event) {
        // Prevent blur
        event.preventDefault();
      }
    };
  }, [handleListboxRef, id]);
  var getOptionProps = useCallback(function (_ref4) {
    var index = _ref4.index,
      option = _ref4.option;
    // eslint-disable-next-line no-console
    // console.log(`---- getOptionProps [${index}] ----`)
    var value = valueRef === null || valueRef === void 0 ? void 0 : valueRef.current;
    var selected = mode === 'select' && multiple && isSelectAll(option, isSelectAllOption, value, options) || (multiple ? Array.isArray(value) ? value : value ? [value] : [] : [value]).some(function (value2) {
      return value2 != null && isOptionEqualToValue(option, value2);
    });
    var disabled = getOptionDisabled ? getOptionDisabled(option) : false;
    return {
      key: "".concat(id, "-option-").concat(index),
      tabIndex: -1,
      role: 'option',
      id: "".concat(id, "-option-").concat(index),
      onMouseOver: handleOptionMouseOver,
      onClick: handleOptionClick,
      onTouchStart: handleOptionTouchStart,
      'data-option-index': index,
      'aria-disabled': disabled,
      'aria-selected': selected
    };
  }, [getOptionDisabled, handleOptionClick, handleOptionMouseOver, handleOptionTouchStart, id, isSelectAllOption, isOptionEqualToValue, mode, multiple, options, valueRef]);
  return {
    getRootProps: getRootProps,
    getInputLabelProps: getInputLabelProps,
    getInputProps: getInputProps,
    getClearProps: getClearProps,
    getPopupIndicatorProps: getPopupIndicatorProps,
    getTagProps: getTagProps,
    getListboxProps: getListboxProps,
    getOptionProps: getOptionProps,
    id: id,
    inputValue: inputValue || '',
    inputValueRef: inputValueRef,
    value: value,
    dirty: dirty,
    popupOpen: popupOpen,
    focused: focused || focusedTag !== -1,
    anchorEl: rootRef.current,
    listboxRef: listboxRef,
    focusedTag: focusedTag,
    groupedOptions: groupedOptions,
    isMultiSelect: isMultiSelect,
    openOnMouseDown: openOnMouseDown,
    handleBlur: handleBlur,
    handlePopupExited: handlePopupExited,
    handlePopupEntering: handlePopupEntering,
    isMaxTagsSelected: isMaxTagsSelected
  };
}

export default useAutocomplete;
//# sourceMappingURL=use-autocomplete.esm.js.map
